/**
 * Session-scoped persistence for the Patient Notification View's roll-up date.
 *
 * The selected date is kept in `sessionStorage` so it survives in-app route
 * navigation (e.g. drilling into a Document Summary and coming back) and page
 * reloads within the same browser tab. It is intentionally *not* persisted in
 * `localStorage`: the value is cleared on logout (see `Sidebar`), dies with the
 * tab session, and additionally expires after {@link SELECTION_TTL_MS}, so a
 * stale selection (e.g. a session left open past midnight) falls back to the
 * default date rather than sticking indefinitely.
 */

const STORAGE_KEY = "ct.notifications.selectedDate";

/** How long a user's date selection stays valid before it expires (20 hours). */
const SELECTION_TTL_MS = 20 * 60 * 60 * 1000;

interface StoredSelection {
  /** The selected date as `YYYY-MM-DD`. */
  date: string;
  /** Epoch milliseconds when the selection was saved (for TTL expiry). */
  savedAt: number;
}

/**
 * Yesterday's date as a local `YYYY-MM-DD` string — the Notification View's
 * default, since the nightly pipeline processes the *previous* day's documents.
 *
 * Uses local calendar fields (not UTC) so it matches the dates produced by the
 * `DateRangeSelector`'s picker and what the user sees in their own timezone.
 *
 * @returns Yesterday's date in `YYYY-MM-DD` format.
 */
export function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Read the user's session-persisted notification date, if one is set and has
 * not expired. An expired or malformed entry is cleared as a side effect.
 *
 * @returns The stored `YYYY-MM-DD` date, or `null` when none is set, it has
 *   expired, or storage is unavailable (e.g. privacy mode).
 */
export function readStoredNotificationDate(): string | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredSelection>;
    const isExpired =
      typeof parsed.savedAt !== "number" || Date.now() - parsed.savedAt > SELECTION_TTL_MS;
    if (!parsed.date || isExpired) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.date;
  } catch {
    // Missing/corrupt entry or storage unavailable — treat as no selection.
    return null;
  }
}

/**
 * Persist the user's chosen notification date for the current session, stamped
 * with the current time so it can expire after {@link SELECTION_TTL_MS}.
 *
 * @param date - The `YYYY-MM-DD` date to remember.
 */
export function writeStoredNotificationDate(date: string): void {
  try {
    const payload: StoredSelection = { date, savedAt: Date.now() };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable (private mode / quota) — fall back to in-memory state.
  }
}

/**
 * Clear the session-persisted notification date. Called on logout so the next
 * login defaults back to yesterday's date.
 */
export function clearStoredNotificationDate(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
